<template>
  <div
    v-if="total > 1"
    class="feature-announcement-dots"
    aria-label="Announcement pagination"
    data-testid="feature-announcement-dots"
  >
    <span
      class="feature-announcement-dots__indicator"
      :style="{ left: `${indicatorLeft}px`, width: `${indicatorWidth}px` }"
    />

    <button
      v-for="index in total"
      ref="dotRefs"
      :key="index"
      type="button"
      class="feature-announcement-dots__dot"
      :aria-label="`Go to announcement ${index} of ${total}`"
      :aria-current="index - 1 === currentIndex"
      :data-testid="`feature-announcement-dots-dot-${index - 1}`"
      @click="emit('select', index - 1)"
    />
  </div>
</template>

<script setup>
const props = defineProps({
  total: {
    type: Number,
    required: true,
  },
  currentIndex: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['select']);

// Vue auto-collects v-for template refs bound to the same ref name into an array,
// in render order, so dotRefs.value[i] lines up with dot index i.
const dotRefs = ref([]);
const indicatorLeft = ref(0);
const indicatorWidth = ref(0);

/**
 * Rather than sliding as a fixed-size dot, the indicator stretches into a pill spanning its old and new
 * position, then catches up and shrinks back to a dot at the new position. A single
 * CSS transition can't produce that intermediate wider state on its own (start/end
 * width are both dot-sized), so it's done in two explicit phases here.
 */
const STRETCH_MS = 150;
let settledIndex = props.currentIndex;
let catchUpTimer = null;

const settleAt = (index) => {
  const el = dotRefs.value[index];
  if (!el) {
    return;
  }
  indicatorLeft.value = el.offsetLeft;
  indicatorWidth.value = el.offsetWidth;
};

const animateTo = (newIndex) => {
  clearTimeout(catchUpTimer);

  const fromEl = dotRefs.value[settledIndex];
  const toEl = dotRefs.value[newIndex];
  if (!fromEl || !toEl) {
    settleAt(newIndex);
    settledIndex = newIndex;
    return;
  }

  const fromLeft = fromEl.offsetLeft;
  const toLeft = toEl.offsetLeft;
  const dotWidth = toEl.offsetWidth;
  const movingForward = toLeft >= fromLeft;

  // Phase 1 (stretch): anchor the leading edge at the current position, grow the
  // pill to span the full distance to the target dot.
  indicatorLeft.value = movingForward ? fromLeft : toLeft;
  indicatorWidth.value = Math.abs(toLeft - fromLeft) + dotWidth;

  catchUpTimer = setTimeout(() => {
    // Phase 2 (catch up): shrink back to dot size, settled at the target position.
    indicatorLeft.value = toLeft;
    indicatorWidth.value = dotWidth;
    settledIndex = newIndex;
  }, STRETCH_MS);
};

watch(() => props.currentIndex, animateTo);
watch(
  () => props.total,
  () =>
    nextTick(() => {
      settleAt(props.currentIndex);
      settledIndex = props.currentIndex;
    }),
);

onMounted(() => {
  settleAt(props.currentIndex);
});

onBeforeUnmount(() => {
  clearTimeout(catchUpTimer);
});
</script>

<style lang="scss" scoped>
.feature-announcement-dots {
  @apply tw-relative tw-flex tw-items-center tw-gap-2;

  &__dot {
    @apply tw-h-2 tw-w-2 tw-cursor-pointer tw-rounded-full tw-border-none tw-bg-black-370 tw-p-0;
  }

  &__indicator {
    @apply tw-absolute tw-top-0 tw-h-2 tw-rounded-full tw-bg-primary-base;
    transition:
      left 0.15s ease-in-out,
      width 0.15s ease-in-out;
  }
}
</style>
